import React, { useState } from 'react';

import { Outlet, useSearchParams, useParams } from 'react-router-dom';

import useMountEffect from '@zenbusiness/application-commons-hooks/useMountEffect';

import useFlowState from '#client/hooks/flow/useFlowState';
import { sanitize, isUUID } from '#client/utils/dataValidation';

/**
 * QuotePersistenceGate component is responsible for managing the cart UUID query parameter required for  Quote Flow.
 * this is because we need to have this info loaded in the flow state before the ecommerceProvider gets mounted.
 *
 * @component
 * @param {object} props - The component props.
 * @param {React.ReactNode} props.children - The child components to be rendered.
 * @returns {React.ReactNode} The rendered child components.
 *
 * @example
 * <QuotePersistenceGate>
 *   <YourComponent />
 * </QuotePersistenceGate>
 */
function QuotePersistenceGate({ children }) {
  const [searchParams] = useSearchParams();
  const { set } = useFlowState();
  const { flowPage } = useParams();
  const [error, setError] = useState(null);
  const [queryParamLoaded, setQueryParamLoaded] = useState(false);

  useMountEffect(() => {
    // the look up for the cartUuid query param should only happen once and the index of the flow
    if (flowPage) {
      setQueryParamLoaded(true);
      return;
    }

    const queryParamCartUuid = searchParams.get('cart_uuid');
    if (queryParamCartUuid) {
      const cartUuid = sanitize(queryParamCartUuid);
      if (!isUUID(cartUuid)) {
        setError(new Error('Invalid cart UUID'));
        return;
      }
      // set the query param cartUuid in the flow state
      set('cartUuid', cartUuid);
    }
    setQueryParamLoaded(true);
  });

  // Throw the error synchronously during render
  if (error) {
    throw error;
  }

  if (!queryParamLoaded) {
    return null;
  }

  return children || <Outlet />;
}

export default QuotePersistenceGate;
